import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Sparkles, CalendarPlus } from "lucide-react";
import { formatDateIT } from "@/lib/dates";
import { fmtHours } from "@/foundation/formatting";
import { DurationInput, DurationLabel } from "@/foundation/duration";

const WEEKDAY_LABELS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const WHICH_OPTIONS = [
  { v: "first",  l: "Il 1° del mese" },
  { v: "second", l: "Il 2° del mese" },
  { v: "third",  l: "Il 3° del mese" },
  { v: "fourth", l: "Il 4° del mese" },
  { v: "last",   l: "L'ultimo del mese" },
  { v: "all",    l: "Tutti del mese" },
];

export default function ScheduleExceptionsCard({ employeeId, employeeName, onExceptionsChanged }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Add single dialog
  const [openSingle, setOpenSingle] = useState(false);
  const [single, setSingle] = useState({ date: "", hours: 4.75, note: "" });

  // Bulk dialog
  const [openBulk, setOpenBulk] = useState(false);
  const [bulk, setBulk] = useState({
    year: now.getFullYear(), weekday: 5, hours: 4.75,
    occurrences_per_month: 1, which_of_month: "first",
    month_from: 1, month_to: 12, note: "Rientro programmato",
  });

  const load = async () => {
    if (!employeeId) { setItems([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${employeeId}/schedule-exceptions`, { params: { year } });
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  // Notifica il parent che le eccezioni sono cambiate (ricalcolo % part-time).
  const notifyChanged = () => {
    if (typeof onExceptionsChanged === "function") onExceptionsChanged();
  };

  // Anni con eccezioni esistenti (per non nascondere anni fuori dalla finestra default)
  const [yearsWithData, setYearsWithData] = useState([]);
  useEffect(() => {
    if (!employeeId) { setYearsWithData([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/employees/${employeeId}/schedule-exceptions`);
        const set = new Set(data.map((x) => Number(x.date?.slice(0, 4))).filter(Boolean));
        setYearsWithData([...set].sort());
      } catch (_) { /* ignore */ }
    })();
  }, [employeeId, items.length]);

  const currentYear = now.getFullYear();
  const yearOptions = Array.from(new Set([
    ...yearsWithData,
    currentYear - 1, currentYear, currentYear + 1, currentYear + 2,
  ])).sort((a, b) => a - b);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId, year]);

  const addSingle = async () => {
    if (!single.date) return toast.error("Inserisci una data");
    try {
      await api.post(`/employees/${employeeId}/schedule-exceptions`, single);
      toast.success("Eccezione aggiunta");
      setOpenSingle(false);
      setSingle({ date: "", hours: 4.75, note: "" });
      await load();
      notifyChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const [bulkBusy, setBulkBusy] = useState(false);
  const addBulk = async () => {
    if (bulkBusy) return;  // anti doppio-clic
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/employees/${employeeId}/schedule-exceptions/bulk`, bulk);
      // Release 1.0.2: risposta strutturata idempotente
      const created = Number(data.created || 0);
      const already = Number(data.already_existing || 0);
      const conflicts = Number(data.conflicts || 0);
      const blocked = Number(data.blocked || 0);
      const parts = [];
      if (created > 0) parts.push(`${created} create`);
      if (already > 0) parts.push(`${already} gia' presenti`);
      if (conflicts > 0) parts.push(`${conflicts} in conflitto`);
      if (blocked > 0) parts.push(`${blocked} bloccate (mese chiuso)`);
      const summary = parts.length ? parts.join(" · ") : "Nessuna nuova eccezione";
      if (conflicts > 0 || blocked > 0) {
        const detail = [];
        if (conflicts > 0) {
          detail.push(
            "Conflitti (valore diverso, non sovrascritto):\n" +
            (data.conflict_dates || []).slice(0, 5).map(c =>
              `• ${c.date}: esistente ${c.existing_hours}h vs richiesto ${c.requested_hours}h`
            ).join("\n")
          );
        }
        if (blocked > 0) {
          detail.push(
            "Mesi bloccati:\n" +
            (data.blocked_dates || []).slice(0, 5).map(b =>
              `• ${b.date} (${b.state})`
            ).join("\n")
          );
        }
        toast.warning(`Generazione completata: ${summary}`, {
          description: detail.join("\n\n"), duration: 8000,
        });
      } else if (created === 0 && already > 0) {
        toast.info(`Nessuna nuova eccezione: ${already} date erano gia' presenti (idempotente).`);
      } else {
        toast.success(`Generazione completata: ${summary}`);
      }
      setOpenBulk(false);
      setYear(bulk.year);
      await load();
      notifyChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.hint || apiErr(e));
      toast.error(msg);
    } finally {
      setBulkBusy(false);
    }
  };

  const [deletingId, setDeletingId] = useState(null);
  const del = async (id) => {
    if (deletingId) return;  // anti doppio-clic
    if (!window.confirm("Eliminare questa eccezione?")) return;
    setDeletingId(id);
    try {
      const { data } = await api.delete(`/schedule-exceptions/${id}`);
      if (data?.already_missing) {
        toast.info("Eccezione gia' rimossa.");
      } else {
        toast.success("Eccezione eliminata.");
      }
      await load();
      notifyChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.hint || apiErr(e));
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const clearYear = async () => {
    if (!window.confirm(`Eliminare TUTTE le eccezioni del ${year} per ${employeeName || "questo dipendente"}?`)) return;
    try {
      const { data } = await api.post("/schedule-exceptions/delete-range", {
        employee_id: employeeId, year, month_from: 1, month_to: 12,
      });
      const deleted = Number(data?.deleted || 0);
      const blocked = (data?.blocked_months || []);
      if (blocked.length > 0) {
        toast.warning(`Eliminate ${deleted} eccezioni. ${blocked.length} mesi bloccati (chiusi).`, {
          description: blocked.map(b => `• ${b.reason}`).join("\n"),
          duration: 8000,
        });
      } else {
        toast.success(`${deleted} eccezioni eliminate.`);
      }
      await load();
      notifyChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.hint || apiErr(e));
      toast.error(msg);
    }
  };

  const totalHours = items.reduce((s, x) => s + Number(x.hours || 0), 0);

  if (!employeeId) return null;

  return (
    <>
      <Card className="border-amber-200 bg-amber-50/40 rounded-md mt-4" data-testid="schedule-exceptions-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-widest text-amber-800 font-semibold">Eccezioni al profilo — {employeeName}</div>
              <div className="text-[11px] text-slate-600 mt-0.5">
                Date specifiche in cui il teorico prevale sulla riga settimanale (es. sabati di rientro, mercoledì alternati).
                Sono <b>ore retribuite teoriche</b>: entrano nel calcolo del monte-ore annuo.
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <Label className="text-[11px] uppercase tracking-widest text-slate-500">Anno</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-24 h-9" data-testid="exceptions-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={() => { setBulk({ ...bulk, year }); setOpenBulk(true); }}
                      className="border-amber-300 text-amber-900 hover:bg-amber-100"
                      data-testid="add-bulk-exceptions-btn">
                <Sparkles size={13} className="mr-1" /> Genera ricorrenti
              </Button>
              <Button size="sm" variant="outline" onClick={() => setOpenSingle(true)}
                      className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                      data-testid="add-single-exception-btn">
                <Plus size={13} className="mr-1" /> Singola
              </Button>
            </div>
          </div>

          <div className="rounded-md bg-white border border-amber-200 overflow-hidden">
            {loading ? (
              <div className="text-center text-sm text-slate-500 py-4">Caricamento…</div>
            ) : items.length === 0 ? (
              <div className="text-center text-sm text-slate-400 py-4">
                Nessuna eccezione nel {year}. Usa &quot;Genera ricorrenti&quot; per aggiungere in un colpo tutti i sabati di rientro.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-amber-100/70">
                  <tr>
                    <th className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-amber-900 font-semibold">Data</th>
                    <th className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-amber-900 font-semibold">Giorno</th>
                    <th className="text-right px-3 py-2 text-[11px] uppercase tracking-widest text-amber-900 font-semibold">Ore</th>
                    <th className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-amber-900 font-semibold">Nota</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-100">
                  {items.map((x) => {
                    const wd = new Date(x.date + "T00:00").getDay();
                    // JS: 0=Domenica, 1=Lunedì → convert
                    const wdIt = wd === 0 ? 6 : wd - 1;
                    return (
                      <tr key={x.id} className="hover:bg-amber-50/50" data-testid={`exception-row-${x.id}`}>
                        <td className="px-3 py-1.5 font-mono">{formatDateIT(x.date)}</td>
                        <td className="px-3 py-1.5">{WEEKDAY_LABELS[wdIt]}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{fmtHours(x.hours)}</td>
                        <td className="px-3 py-1.5 text-slate-600">{x.note || "—"}</td>
                        <td className="px-3 py-1.5 text-right">
                          <Button size="icon" variant="ghost" onClick={() => del(x.id)}
                                  disabled={deletingId === x.id}
                                  className="h-7 w-7 text-red-700 hover:bg-red-50 disabled:opacity-40"
                                  data-testid={`delete-exception-${x.id}`}>
                            <Trash2 size={13} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {items.length > 0 && (
            <div className="flex items-center justify-between text-xs text-slate-600 pt-1">
              <div><b>{items.length}</b> eccezioni · totale <b>{fmtHours(totalHours)} h</b></div>
              <Button size="sm" variant="ghost" onClick={clearYear}
                      className="h-7 text-red-700 hover:bg-red-50"
                      data-testid="clear-year-exceptions-btn">
                Reset anno {year}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog SINGOLA */}
      <Dialog open={openSingle} onOpenChange={setOpenSingle}>
        <DialogContent formDialog data-testid="single-exception-dialog">
          <DialogHeader>
            <DialogTitle>Aggiungi eccezione</DialogTitle>
            <DialogDescription>
              Una data specifica in cui il teorico è diverso dal profilo settimanale.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Data</Label>
              <Input type="date" value={single.date}
                     onChange={(e) => setSingle({ ...single, date: e.target.value })}
                     data-testid="single-exception-date" />
            </div>
            <div>
              <DurationLabel data-testid="single-exception-hours">Ore teoriche</DurationLabel>
              <DurationInput value={single.hours}
                     onCommit={(n) => setSingle((s) => ({ ...s, hours: n ?? 0 }))}
                     data-testid="single-exception-hours" />
            </div>
            <div>
              <Label>Nota (opzionale)</Label>
              <Input value={single.note}
                     onChange={(e) => setSingle({ ...single, note: e.target.value })}
                     placeholder="es. Rientro sabato marzo"
                     data-testid="single-exception-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenSingle(false)}>Annulla</Button>
            <Button onClick={addSingle} className="bg-emerald-800 hover:bg-emerald-700 text-white"
                    data-testid="save-single-exception-btn">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog BULK ricorrente */}
      <Dialog open={openBulk} onOpenChange={setOpenBulk}>
        <DialogContent formDialog className="max-w-lg" data-testid="bulk-exception-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus size={18} /> Genera eccezioni ricorrenti
            </DialogTitle>
            <DialogDescription>
              Es. &quot;Il 1° sabato di ogni mese del 2026 → 4,75h&quot; oppure &quot;I primi 2 sabati di ogni mese&quot;.
              Puoi limitare i mesi (default gennaio-dicembre).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Anno</Label>
                <Input type="number" value={bulk.year}
                       onChange={(e) => setBulk({ ...bulk, year: Number(e.target.value) })}
                       data-testid="bulk-year" />
              </div>
              <div>
                <Label>Giorno della settimana</Label>
                <Select value={String(bulk.weekday)} onValueChange={(v) => setBulk({ ...bulk, weekday: Number(v) })}>
                  <SelectTrigger data-testid="bulk-weekday"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WEEKDAY_LABELS.map((l, i) => (
                      <SelectItem key={i} value={String(i)}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ricorrenza</Label>
                <Select value={bulk.which_of_month} onValueChange={(v) => setBulk({ ...bulk, which_of_month: v })}>
                  <SelectTrigger data-testid="bulk-which"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WHICH_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>N. occorrenze per mese</Label>
                <Input type="number" min="1" max="4" value={bulk.occurrences_per_month}
                       onChange={(e) => setBulk({ ...bulk, occurrences_per_month: Number(e.target.value) })}
                       data-testid="bulk-occurrences" />
                <div className="text-[10px] text-slate-500 mt-0.5">Ignorato se ricorrenza è &quot;specifica&quot; (1°, 2° …)</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <DurationLabel data-testid="bulk-hours">Ore teoriche</DurationLabel>
                <DurationInput value={bulk.hours}
                       onCommit={(n) => setBulk((b) => ({ ...b, hours: n ?? 0 }))}
                       data-testid="bulk-hours" />
              </div>
              <div>
                <Label>Nota</Label>
                <Input value={bulk.note}
                       onChange={(e) => setBulk({ ...bulk, note: e.target.value })}
                       data-testid="bulk-note" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Da mese</Label>
                <Input type="number" min="1" max="12" value={bulk.month_from}
                       onChange={(e) => setBulk({ ...bulk, month_from: Number(e.target.value) })} />
              </div>
              <div>
                <Label>A mese</Label>
                <Input type="number" min="1" max="12" value={bulk.month_to}
                       onChange={(e) => setBulk({ ...bulk, month_to: Number(e.target.value) })} />
              </div>
            </div>

            <div className="text-xs text-amber-900 bg-amber-100/70 border border-amber-200 rounded-md px-3 py-2">
              <b>Anteprima:</b> {WHICH_OPTIONS.find((o) => o.v === bulk.which_of_month)?.l.toLowerCase()} — <b>{WEEKDAY_LABELS[bulk.weekday]}</b> —
              {" "}con <b>{fmtHours(bulk.hours)} h</b> teoriche,
              da <b>{bulk.month_from}/{bulk.year}</b> a <b>{bulk.month_to}/{bulk.year}</b>.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenBulk(false)} disabled={bulkBusy}>Annulla</Button>
            <Button onClick={addBulk} disabled={bulkBusy}
                    className="bg-emerald-800 hover:bg-emerald-700 text-white disabled:opacity-60"
                    data-testid="save-bulk-exceptions-btn">
              {bulkBusy ? "Generazione in corso…" : "Genera eccezioni"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
